import { useCallback, useContext, useState } from 'react'
import FirebaseContext from '../../contexts/FirebaseContext'
import apiUrl from '../../Root/Providers/ApolloProvider/client/url'

const statementEndpoint = `${apiUrl}/invoices/statement-of-account`

const normalizeFileName = (value) => {
  const sanitized = (value || '')
    .replace(/^UTF-8''/i, '')
    .replace(/^['"]|['"]$/g, '')
    .replace(/[\\/:*?"<>|]/g, '-')
    .split('')
    .filter((character) => character.charCodeAt(0) >= 32)
    .join('')
    .replace(/\s+/g, ' ')
    .trim()

  if (!sanitized || sanitized === '.' || sanitized === '..') return ''
  return /\.pdf$/i.test(sanitized) ? sanitized : `${sanitized}.pdf`
}

export const getFileNameFromDisposition = (contentDisposition, fallbackName) => {
  let value = ''
  const encodedMatch = contentDisposition && contentDisposition.match(/filename\*\s*=\s*([^;]+)/i)
  const plainMatch = contentDisposition && contentDisposition.match(/filename\s*=\s*([^;]+)/i)

  if (encodedMatch) {
    try {
      value = decodeURIComponent(encodedMatch[1].trim().replace(/^UTF-8''/i, '').replace(/^['"]|['"]$/g, ''))
    } catch (error) {
      value = ''
    }
  }
  if (!value && plainMatch) value = plainMatch[1].trim().replace(/^['"]|['"]$/g, '')

  return normalizeFileName(value) || normalizeFileName(fallbackName)
}

const isPdfResponse = async (response, blob) => {
  const contentType = (response.headers.get('content-type') || '').split(';')[0].trim().toLowerCase()
  if (contentType === 'application/pdf') return true
  const signature = new TextDecoder().decode(await blob.slice(0, 4).arrayBuffer())
  return signature === '%PDF'
}

const createRequestUrl = ({ userId, parentUserId, statementFrom, statementTo, status = [] }) => {
  const searchParams = new URLSearchParams()
  searchParams.append('userId', userId)
  if (parentUserId) searchParams.append('parentUserId', parentUserId)
  if (statementFrom) searchParams.append('statementFrom', statementFrom)
  if (statementTo) searchParams.append('statementTo', statementTo)
  status.filter(Boolean).forEach((item) => searchParams.append('status', item))
  return `${statementEndpoint}?${searchParams.toString()}`
}

const downloadBlob = (blob, fileName) => {
  const objectUrl = window.URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = fileName
  anchor.style.display = 'none'
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 0)
}

const getResponseErrorMessage = async (response) => {
  const responseText = await response.text()
  if (!responseText) return ''

  try {
    const payload = JSON.parse(responseText)
    return typeof payload?.message === 'string' ? payload.message.trim() : ''
  } catch (error) {
    return responseText.trim()
  }
}

export default function useGetStatementAccount () {
  const firebase = useContext(FirebaseContext)
  const [pendingRequests, setPendingRequests] = useState(0)

  const downloadStatement = useCallback(async (params) => {
    setPendingRequests((count) => count + 1)
    try {
      const currentUser = firebase?.auth?.().currentUser
      if (!currentUser) throw new Error('Unable to authenticate statement request.')
      const token = await currentUser.getIdToken()
      const response = await window.fetch(createRequestUrl(params), {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!response.ok) {
        const backendMessage = await getResponseErrorMessage(response)
        throw new Error(backendMessage || `Statement request failed with status ${response.status}.`)
      }

      const blob = await response.blob()
      if (!blob || !blob.size || !(await isPdfResponse(response, blob))) {
        throw new Error('Statement response is not a valid PDF.')
      }

      const fallbackName = `statement-of-account-${params.userId}.pdf`
      const fileName = getFileNameFromDisposition(response.headers.get('content-disposition'), fallbackName)
      downloadBlob(blob, fileName)
      return { userId: params.userId, fileName }
    } finally {
      setPendingRequests((count) => Math.max(0, count - 1))
    }
  }, [firebase])

  return { downloadStatement, loading: pendingRequests > 0 }
}
